import iconCaretDown from '@phosphor-icons/core/regular/caret-down.svg';
import iconListDashes from '@phosphor-icons/core/regular/list-dashes.svg';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useFeatures } from '@/hooks/use-features';
import { useOwnAccount } from '@/hooks/use-own-account';
import { useAntennas } from '@/queries/accounts/use-antennas';
import { useCircles } from '@/queries/accounts/use-circles';
import { useLists } from '@/queries/accounts/use-lists';
import { useInstance } from '@/stores/instance';
import { useSettings } from '@/stores/settings';

import DropdownMenu, { type Menu } from './dropdown-menu';
import Icon from './ui/icon';

const messages = defineMessages({
  homeTimeline: { id: 'column.home', defaultMessage: 'Home' },
  localTimeline: { id: 'tabs_bar.local', defaultMessage: 'Local' },
  bubbleTimeline: { id: 'column.bubble', defaultMessage: 'Bubble timeline' },
  federatedTimeline: { id: 'tabs_bar.fediverse', defaultMessage: 'Fediverse' },
  wrenchedTimeline: { id: 'column.wrenched', defaultMessage: 'Recent wrenches timeline' },
  lists: { id: 'column.lists', defaultMessage: 'Lists' },
  manageLists: { id: 'lists.manage', defaultMessage: 'Manage lists' },
});

const useTimelineHeading = (active: ITimelinePicker['active'] | null) => {
  const intl = useIntl();
  const { data: lists } = useLists(active?.startsWith('list:'));
  const { data: circles } = useCircles(active?.startsWith('circle:'));
  const { data: antennas } = useAntennas(active?.startsWith('antenna:'));

  return useMemo(() => {
    switch (active) {
      case 'home':
        return intl.formatMessage(messages.homeTimeline);
      case 'local':
        return intl.formatMessage(messages.localTimeline);
      case 'bubble':
        return intl.formatMessage(messages.bubbleTimeline);
      case 'federated':
        return intl.formatMessage(messages.federatedTimeline);
      case 'wrenched':
        return intl.formatMessage(messages.wrenchedTimeline);
      default:
        if (active?.startsWith('list:')) {
          const list = lists?.find((list) => `list:${list.id}` === active);
          return list?.title ?? '';
        }
        if (active?.startsWith('circle:')) {
          const circle = circles?.find((circle) => `circle:${circle.id}` === active);
          return circle?.title ?? '';
        }
        if (active?.startsWith('antenna:')) {
          const antenna = antennas?.find((antenna) => `antenna:${antenna.id}` === active);
          return antenna?.title ?? '';
        }
        if (active?.startsWith('instance:')) {
          return active.replace('instance:', '');
        }
        return '';
    }
  }, [active, lists, circles, antennas]);
};

interface ITimelinePicker {
  active:
    | 'home'
    | 'local'
    | 'bubble'
    | 'federated'
    | 'wrenched'
    | `list:${string}`
    | `circle:${string}`
    | `antenna:${string}`
    | `instance:${string}`;
}

interface ITimelineNavigation extends ITimelinePicker {
  action?: React.ReactNode;
}

const canAccessTimeline = (
  access: 'public' | 'authenticated' | 'restricted' | 'disabled',
  isLoggedIn: boolean,
  isAdmin: boolean,
) =>
  isLoggedIn ? (access === 'restricted' ? isAdmin : access !== 'disabled') : access === 'public';

/** Navigation shared by the home, local, Fediverse, and list timelines. */
const TimelineNavigation: React.FC<ITimelineNavigation> = ({ active, action }) => {
  const intl = useIntl();
  const features = useFeatures();
  const { data: account } = useOwnAccount();
  const isLoggedIn = !!account;
  const isAdmin = !!account?.is_admin || !!account?.is_moderator;
  const instance = useInstance();
  const timelineAccess = instance.configuration.timelines_access;
  const { defaultTimeline } = useSettings();
  const { data: lists } = useLists();

  const navigationItems = useMemo(() => {
    const items = [];

    if (isLoggedIn) {
      items.push({
        active: active === 'home',
        to: defaultTimeline === 'home' ? ('/' as const) : ('/timeline/home' as const),
        text: intl.formatMessage(messages.homeTimeline),
      });
    }

    if (
      features.publicTimeline &&
      features.federating &&
      canAccessTimeline(timelineAccess.live_feeds.local, isLoggedIn, isAdmin)
    ) {
      items.push({
        active: active === 'local',
        to: '/timeline/local' as const,
        text: instance.title,
      });
    }

    if (
      features.publicTimeline &&
      features.federating &&
      canAccessTimeline(timelineAccess.live_feeds.remote, isLoggedIn, isAdmin)
    ) {
      items.push({
        active: active === 'federated',
        to: '/timeline/fediverse' as const,
        text: intl.formatMessage(messages.federatedTimeline),
      });
    }

    return items;
  }, [
    active,
    defaultTimeline,
    features,
    instance.title,
    intl,
    isAdmin,
    isLoggedIn,
    timelineAccess,
  ]);

  const listItems = useMemo(() => {
    const items: Menu = (lists ?? []).map((list) => ({
      to: '/list/$listId',
      params: { listId: list.id },
      text: list.title,
      icon: iconListDashes,
      active: active === `list:${list.id}`,
    }));

    if (items.length) items.push(null);
    items.push({
      to: '/lists',
      text: intl.formatMessage(messages.manageLists),
      icon: iconListDashes,
    });

    return items;
  }, [active, intl, lists]);

  return (
    <nav className='timeline-navigation' aria-label={intl.formatMessage(messages.homeTimeline)}>
      <div className='timeline-navigation__tabs'>
        {navigationItems.map((item) => (
          <Link
            key={item.to}
            className={clsx('timeline-navigation__item', {
              'timeline-navigation__item--active': item.active,
            })}
            to={item.to}
            aria-current={item.active ? 'page' : undefined}
          >
            {item.text}
          </Link>
        ))}

        {isLoggedIn && features.lists && (
          <DropdownMenu items={listItems} width='16rem' placement='bottom-end' forceDropdown>
            <button
              className={clsx('timeline-navigation__item', {
                'timeline-navigation__item--active': active.startsWith('list:'),
              })}
              type='button'
            >
              {intl.formatMessage(messages.lists)}
              <Icon src={iconCaretDown} aria-hidden />
            </button>
          </DropdownMenu>
        )}
      </div>

      {action && <div className='timeline-navigation__action'>{action}</div>}
    </nav>
  );
};

export { TimelineNavigation, useTimelineHeading, type ITimelinePicker };
